<template>
  <div class="region-management">
    <div class="page-header">
      <div class="header-left">
        <h2>区域管理</h2>
        <p class="header-desc">管理出发地和目的地区域信息，支持按省市筛选。</p>
      </div>
      <div class="header-actions">
        <el-button type="primary" @click="handleCreate">
          <i class="el-icon-plus"></i> 新增区域
        </el-button>
      </div>
    </div>

    <div class="config-container">
      <!-- 搜索筛选 -->
      <div class="search-bar">
        <span class="search-label">区域类型：</span>
        <el-select
          v-model="filters.regionType"
          placeholder="全部"
          class="search-select"
          @change="handleSearch"
        >
          <el-option label="全部" value="" />
          <el-option label="出发地" :value="1" />
          <el-option label="目的地" :value="2" />
        </el-select>
        <span class="search-label">省份：</span>
        <el-select
          v-model="filters.provinceCode"
          placeholder="请选择/搜索省份"
          class="search-select"
          filterable
          @change="handleProvinceFilterChange"
        >
          <el-option label="全部" value="" />
          <el-option
            v-for="item in provinceList"
            :key="item.code"
            :label="item.name"
            :value="item.code"
          />
        </el-select>
        <span class="search-label">城市：</span>
        <el-select
          v-model="filters.cityCode"
          placeholder="请选择/搜索城市"
          class="search-select"
          filterable
          @change="handleSearch"
          :disabled="!filters.provinceCode"
        >
          <el-option label="全部" value="" />
          <el-option
            v-for="item in cityList"
            :key="item.code"
            :label="item.name"
            :value="item.code"
          />
        </el-select>
      </div>

      <!-- 表格 -->
      <el-table :data="list" style="width: 100%" v-loading="loading" border>
        <el-table-column label="序号" width="60" type="index" />
        <el-table-column label="区域类型" width="100">
          <template slot-scope="scope">
            <el-tag :type="scope.row.regionType === 1 ? 'success' : 'warning'">
              {{ scope.row.regionType === 1 ? '出发地' : '目的地' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="省份" width="140">
          <template slot-scope="scope">
            {{ scope.row.provinceName }}
          </template>
        </el-table-column>
        <el-table-column label="城市" width="140">
          <template slot-scope="scope">
            {{ scope.row.cityName }}
          </template>
        </el-table-column>
        <el-table-column label="区域名称" min-width="200">
          <template slot-scope="scope">
            {{ scope.row.district }}
          </template>
        </el-table-column>
        <el-table-column label="创建时间" width="160">
          <template slot-scope="scope">
            {{ scope.row.createTime | formatDate }}
          </template>
        </el-table-column>
        <el-table-column label="更新时间" width="160">
          <template slot-scope="scope">
            {{ scope.row.updateTime | formatDate }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="220" fixed="right">
          <template slot-scope="scope">
            <el-button type="text" @click="handleView(scope.row)">查看</el-button>
            <el-button type="text" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button type="text" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>

      <!-- 分页 -->
      <div class="pagination">
        <span class="pagination-info">
          显示第 {{ (currentPage - 1) * pageSize + 1 }} 至
          {{ Math.min(currentPage * pageSize, total) }} 条，共 {{ total }} 条记录
        </span>
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
          :current-page="currentPage"
          :page-sizes="[10, 20, 50]"
          :page-size="pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="total"
        />
      </div>
    </div>

    <!-- 弹窗 -->
    <el-dialog
      :title="dialogTitle"
      :visible.sync="dialogVisible"
      width="560px"
      :close-on-click-modal="false"
      @close="handleDialogClose"
    >
      <el-form
        ref="regionForm"
        :model="regionForm"
        :rules="regionRules"
        label-width="100px"
      >
        <el-form-item label="区域类型" prop="regionType">
          <el-select
            v-model="regionForm.regionType"
            placeholder="请选择区域类型"
            style="width: 100%"
            :disabled="isViewMode"
          >
            <el-option label="出发地" :value="1" />
            <el-option label="目的地" :value="2" />
          </el-select>
        </el-form-item>
        <el-form-item label="省份" prop="provinceCode">
          <el-select
            v-model="regionForm.provinceCode"
            placeholder="请选择/搜索省份"
            style="width: 100%"
            :disabled="isViewMode"
            filterable
            @change="handleFormProvinceChange"
          >
            <el-option
              v-for="item in provinceList"
              :key="item.code"
              :label="item.name"
              :value="item.code"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="城市" prop="cityCode">
          <el-select
            v-model="regionForm.cityCode"
            placeholder="请选择/搜索城市"
            style="width: 100%"
            :disabled="!regionForm.provinceCode || isViewMode"
            filterable
          >
            <el-option
              v-for="item in formCityList"
              :key="item.code"
              :label="item.name"
              :value="item.code"
            />
          </el-select>
        </el-form-item>
        <el-form-item label="区域名称" prop="district">
          <el-input
            v-model="regionForm.district"
            placeholder="请输入区域名称/具体地点"
            :disabled="isViewMode"
            maxlength="100"
            show-word-limit
          />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">{{ isViewMode ? '关闭' : '取消' }}</el-button>
        <el-button
          v-if="!isViewMode"
          type="primary"
          @click="handleSubmit"
          :loading="submitLoading"
        >确认保存</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
export default {
  name: 'RegionManagement',
  data() {
    return {
      loading: false,
      list: [],
      total: 0,
      currentPage: 1,
      pageSize: 20,
      filters: {
        regionType: '',
        provinceCode: '',
        cityCode: ''
      },
      // 弹窗
      dialogVisible: false,
      isViewMode: false,
      isEditMode: false,
      submitLoading: false,
      regionForm: {
        id: null,
        regionType: null,
        provinceCode: '',
        provinceName: '',
        cityCode: '',
        cityName: '',
        district: ''
      },
      regionRules: {
        regionType: [{ required: true, message: '请选择区域类型', trigger: 'change' }],
        provinceCode: [{ required: true, message: '请选择省份', trigger: 'change' }],
        cityCode: [{ required: true, message: '请选择城市', trigger: 'change' }],
        district: [
          { required: true, message: '请输入区域名称', trigger: 'blur' },
          { max: 100, message: '区域名称不能超过100个字符', trigger: 'blur' }
        ]
      },
      // 下拉选项
      provinceList: [],
      cityList: [],          // 筛选区域的城市列表
      formCityList: []       // 表单区域的城市列表
    }
  },
  computed: {
    dialogTitle() {
      if (this.isViewMode) return '查看区域详情'
      if (this.isEditMode) return '编辑区域'
      return '新增区域'
    }
  },
  mounted() {
    this.fetchList()
    this.loadProvinceList()
  },
  methods: {
    // ===== 列表相关 =====
    async fetchList() {
      this.loading = true
      try {
        const params = {
          pageNum: this.currentPage,
          pageSize: this.pageSize
        }
        if (this.filters.regionType !== '') {
          params.regionType = this.filters.regionType
        }
        if (this.filters.provinceCode) {
          params.provinceCode = this.filters.provinceCode
        }
        if (this.filters.cityCode) {
          params.cityCode = this.filters.cityCode
        }
        const res = await this.$http.get('region/getRegionList.do', { params })
        if (res.code === '0' || res.code === 0) {
          this.list = res.result || []
          this.total = res.total || 0
        } else {
          this.$message.error(res.message || '获取区域列表失败')
        }
      } catch (error) {
        console.error('获取区域列表失败:', error)
        this.$message.error('网络请求异常')
      } finally {
        this.loading = false
      }
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.fetchList()
    },
    handleCurrentChange(val) {
      this.currentPage = val
      this.fetchList()
    },
    handleSearch() {
      this.currentPage = 1
      this.fetchList()
    },
    handleProvinceFilterChange(provinceCode) {
      this.filters.cityCode = ''
      if (provinceCode) {
        this.loadCityList(provinceCode, 'filter')
      } else {
        this.cityList = []
      }
      this.handleSearch()
    },

    // ===== 省市数据加载 =====
    async loadProvinceList() {
      try {
        const res = await this.$http.get('/region/getAllProvinces.do')
        if (res.code === '0' || res.code === 0) {
          this.provinceList = res.result || []
        }
      } catch (error) {
        console.error('加载省份列表失败:', error)
      }
    },
    async loadCityList(provinceCode, target = 'filter') {
      try {
        const res = await this.$http.get('/region/getCitiesByProvince.do', {
          params: { provinceCode }
        })
        if (res.code === '0' || res.code === 0) {
          if (target === 'filter') {
            this.cityList = res.result || []
          } else {
            this.formCityList = res.result || []
          }
        }
      } catch (error) {
        console.error('加载城市列表失败:', error)
      }
    },

    // ===== 表单相关 =====
    resetForm() {
      this.regionForm = {
        id: null,
        regionType: null,
        provinceCode: '',
        provinceName: '',
        cityCode: '',
        cityName: '',
        district: ''
      }
      this.formCityList = []
    },
    handleCreate() {
      this.isViewMode = false
      this.isEditMode = false
      this.resetForm()
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.regionForm && this.$refs.regionForm.clearValidate()
      })
    },
    async handleEdit(row) {
      this.isViewMode = false
      this.isEditMode = true
      await this.loadDetail(row.id)
      this.dialogVisible = true
      this.$nextTick(() => {
        this.$refs.regionForm && this.$refs.regionForm.clearValidate()
      })
    },
    async handleView(row) {
      this.isViewMode = true
      this.isEditMode = false
      await this.loadDetail(row.id)
      this.dialogVisible = true
    },
    async loadDetail(id) {
      try {
        const res = await this.$http.get('region/getRegionDetail.do', {
          params: { id }
        })
        if (res.code === '0' || res.code === 0) {
          const data = res.result || {}
          this.regionForm = {
            id: data.id,
            regionType: data.regionType,
            provinceCode: data.provinceCode || '',
            provinceName: data.provinceName || '',
            cityCode: data.cityCode || '',
            cityName: data.cityName || '',
            district: data.district || ''
          }
          // 加载对应省份的城市列表用于回显
          if (data.provinceCode) {
            await this.loadCityList(data.provinceCode, 'form')
          }
        } else {
          this.$message.error(res.message || '获取区域详情失败')
        }
      } catch (error) {
        console.error('获取区域详情失败:', error)
        this.$message.error('网络请求异常')
      }
    },
    handleFormProvinceChange(provinceCode) {
      this.regionForm.cityCode = ''
      this.regionForm.cityName = ''
      this.regionForm.provinceName = ''
      // 设置省份名称
      const province = this.provinceList.find((p) => p.code === provinceCode)
      if (province) {
        this.regionForm.provinceName = province.name
      }
      if (provinceCode) {
        this.loadCityList(provinceCode, 'form')
      } else {
        this.formCityList = []
      }
    },
    handleDialogClose() {
      this.isViewMode = false
      this.isEditMode = false
      this.resetForm()
    },
    async handleSubmit() {
      try {
        await this.$refs.regionForm.validate()

        // 设置城市名称
        const city = this.formCityList.find((c) => c.code === this.regionForm.cityCode)
        if (city) {
          this.regionForm.cityName = city.name
        }
        // 如果省份名称还没设置，补一下
        if (!this.regionForm.provinceName) {
          const province = this.provinceList.find((p) => p.code === this.regionForm.provinceCode)
          if (province) {
            this.regionForm.provinceName = province.name
          }
        }

        this.submitLoading = true
        let res
        if (this.isEditMode) {
          res = await this.$http.post('region/updateRegion.do', this.regionForm)
        } else {
          res = await this.$http.post('region/addRegion.do', this.regionForm)
        }

        if (res.code === '0' || res.code === 0) {
          this.$message.success(this.isEditMode ? '修改成功' : '新增成功')
          this.dialogVisible = false
          this.fetchList()
        } else {
          this.$message.error(res.message || '保存失败')
        }
      } catch (error) {
        if (error !== false) {
          console.error('保存区域失败:', error)
          if (error && error.message && error.message.indexOf('网络') === -1) {
            // 表单校验不通过的异常不提示
          } else {
            this.$message.error('网络请求异常')
          }
        }
      } finally {
        this.submitLoading = false
      }
    },

    // ===== 删除 =====
    handleDelete(row) {
      this.$confirm(
        `确定要删除区域「${row.provinceName} ${row.cityName} ${row.district}」吗？`,
        '提示',
        {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }
      )
        .then(async () => {
          try {
            const res = await this.$http.get('region/deleteRegion.do', {
              params: { id: row.id }
            })
            if (res.code === '0' || res.code === 0) {
              this.$message.success('删除成功')
              this.fetchList()
            } else {
              this.$message.error(res.message || '删除失败')
            }
          } catch (error) {
            console.error('删除失败:', error)
            this.$message.error('网络请求异常')
          }
        })
        .catch(() => {})
    }
  },
  filters: {
    formatDate(value) {
      if (value === null || value === undefined || value === '') return '-'
      if (typeof value === 'number' || /^\d+$/.test(String(value))) {
        const numValue = typeof value === 'number' ? value : Number(value)
        const date = new Date(numValue)
        if (isNaN(date.getTime())) return '-'
        const year = date.getFullYear()
        const month = String(date.getMonth() + 1).padStart(2, '0')
        const day = String(date.getDate()).padStart(2, '0')
        const hours = String(date.getHours()).padStart(2, '0')
        const minutes = String(date.getMinutes()).padStart(2, '0')
        return `${year}-${month}-${day} ${hours}:${minutes}`
      }
      if (typeof value === 'string') {
        if (/^\d+$/.test(value.trim())) {
          const date = new Date(Number(value))
          if (!isNaN(date.getTime())) {
            const year = date.getFullYear()
            const month = String(date.getMonth() + 1).padStart(2, '0')
            const day = String(date.getDate()).padStart(2, '0')
            const hours = String(date.getHours()).padStart(2, '0')
            const minutes = String(date.getMinutes()).padStart(2, '0')
            return `${year}-${month}-${day} ${hours}:${minutes}`
          }
        }
        return value.replace('T', ' ').substring(0, 16)
      }
      return '-'
    }
  }
}
</script>

<style scoped>
.region-management {
  padding: 20px 24px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 80px);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.header-left h2 {
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #1f2937;
}

.header-desc {
  margin: 4px 0 0 0;
  font-size: 13px;
  color: #6b7280;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.config-container {
  background: #ffffff;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  padding: 20px;
}

.search-bar {
  display: flex;
  gap: 12px;
  margin-bottom: 20px;
  flex-wrap: wrap;
  align-items: center;
}

.search-label {
  font-size: 14px;
  color: #374151;
}

.search-select {
  width: 140px;
}

.pagination {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 16px;
  border-top: 1px solid #f0f0f0;
  background-color: #fafafa;
}

.pagination-info {
  font-size: 13px;
  color: #6b7280;
}

.pagination .el-pagination {
  margin: 0;
}

.pagination .el-pagination button {
  border-radius: 6px;
}

.pagination .el-pagination .el-pager li.active {
  background-color: #22c55e;
  border-color: #22c55e;
  color: #fff;
}

.pagination .el-pagination .el-pager li.active:hover {
  background-color: #16a34a;
}
</style>
